import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader } from "../components/common";
import { Eye, Users, TrendingUp, FileSpreadsheet, FileDown } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center mb-4 ${accent}`}><Icon className="h-5 w-5" /></div>
      <p className="font-display text-3xl font-black text-slate-900">{value}</p>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
    </div>
  );
}

export default function AdminTraffic() {
  const [t, setT] = useState(null);

  useEffect(() => { api.get("/admin/traffic").then(({ data }) => setT(data)); }, []);
  if (!t) return null;

  const stamp = () => new Date().toISOString().slice(0, 10);
  const summaryRows = [
    ["Total Page Visits", t.total_visits],
    ["Unique Visitors", t.unique_visitors],
  ];

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const sumWs = XLSX.utils.aoa_to_sheet([["Metric", "Value"], ...summaryRows]);
    sumWs["!cols"] = [{ wch: 24 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, sumWs, "Summary");
    const dayWs = XLSX.utils.aoa_to_sheet([["Date", "Visits"], ...(t.visits_by_day || []).map((d) => [d.name, d.count])]);
    XLSX.utils.book_append_sheet(wb, dayWs, "Visits by Day");
    const pgWs = XLSX.utils.aoa_to_sheet([["Page", "Visits"], ...(t.top_pages || []).map((d) => [d.name, d.count])]);
    pgWs["!cols"] = [{ wch: 36 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, pgWs, "Top Pages");
    XLSX.writeFile(wb, `assmang-site-traffic-${stamp()}.xlsx`);
  };

  const exportPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Assmang SMME Portal — Site Traffic", 14, 16);
    doc.setFontSize(9);
    doc.text(`Exported ${stamp()}`, 14, 22);
    autoTable(doc, {
      startY: 26, head: [["Metric", "Value"]], body: summaryRows.map(([k, v]) => [k, String(v)]),
      styles: { fontSize: 9, cellPadding: 2 }, headStyles: { fillColor: [0, 66, 122] },
    });
    doc.setFontSize(11); doc.text("Visits by Day", 14, doc.lastAutoTable.finalY + 10);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 13, head: [["Date", "Visits"]],
      body: (t.visits_by_day || []).map((d) => [d.name, String(d.count)]),
      styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [0, 66, 122] },
    });
    doc.setFontSize(11); doc.text("Top Pages", 14, doc.lastAutoTable.finalY + 10);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 13, head: [["Page", "Visits"]],
      body: (t.top_pages || []).map((d) => [d.name, String(d.count)]),
      styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [0, 66, 122] },
    });
    doc.save(`assmang-site-traffic-${stamp()}.pdf`);
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader eyebrow="Administrator" title="Site Traffic"
          subtitle="Visitor analytics across the public site and portal." />
        <div className="flex gap-2">
          <button data-testid="traffic-export-excel" onClick={exportExcel}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#1D6F42] text-white text-sm font-medium hover:bg-[#165a35] transition-colors">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </button>
          <button data-testid="traffic-export-pdf" onClick={exportPdf}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#B3261E] text-white text-sm font-medium hover:bg-[#8f1e18] transition-colors">
            <FileDown className="h-4 w-4" /> PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div data-testid="traffic-total-visits-card"><Stat icon={Eye} label="Total Page Visits" value={t.total_visits} accent="bg-blue-50 text-blue-700" /></div>
        <div data-testid="traffic-unique-card"><Stat icon={Users} label="Unique Visitors" value={t.unique_visitors} accent="bg-emerald-50 text-emerald-700" /></div>
        <Stat icon={TrendingUp} label="Days Tracked" value={(t.visits_by_day || []).length} accent="bg-[#FEF9E7] text-[#F2B50C]" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <h3 className="font-display font-bold text-slate-900 mb-4">Visits Over Time</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={t.visits_by_day}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="count" fill="#00427A" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <h3 className="font-display font-bold text-slate-900 px-6 pt-6 pb-4">Most Visited Pages</h3>
        {(t.top_pages || []).length === 0 ? (
          <p className="px-6 pb-6 text-sm text-slate-400">No visits recorded yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y border-slate-200 text-left text-slate-500">
              <tr><th className="px-5 py-3 font-semibold">Page</th><th className="px-5 py-3 font-semibold text-right">Visits</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {t.top_pages.map((p, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono-data text-slate-700">{p.name}</td>
                  <td className="px-5 py-3 text-right font-semibold">{p.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
